<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue';

interface SearchInputProps {
	placeholder?: string;
	disabled?: boolean;
	loading?: boolean;
}

defineProps<SearchInputProps>();

const searchValue = defineModel<string>({ default: '' });

const isExpanded = ref(!!searchValue.value);
const isButtonShown = ref(!searchValue.value);
const inputRef = useTemplateRef('input');

watch(searchValue, (newValue) => {
	if (newValue && !isExpanded.value) {
		expandSearch();
	}
}, { immediate: true });

function expandSearch() {
	isExpanded.value = true;
	isButtonShown.value = false;
}

function collapseSearch() {
	if (!searchValue.value) {
		isExpanded.value = false;
	}
	else {
		searchValue.value = '';
	}
}

function clearSearch() {
	searchValue.value = '';
	inputRef.value?.inputRef?.focus();
}

function handleBlur() {
	if (!searchValue.value) {
		collapseSearch();
	}
}
</script>

<template>
	<div class="flex h-6 items-center">
		<Transition
			enter-active-class="transition-all duration-100 ease-out"
			enter-from-class="w-0 opacity-0"
			enter-to-class="w-content opacity-100"
			leave-active-class="transition-all duration-100 ease-in [&_.trailing]:transition-opacity [&_.trailing]:duration-100 [&_input]:transition-all [&_input]:duration-100 [&_input]:delay-20 [&_input]:placeholder:transition-all [&_input]:placeholder:delay-10 [&_input]:placeholder:duration-100 [&_.leading]:transition-all [&_.leading]:duration-100 [&_.leading]:delay-10 [&_.leadingIcon]:transition-colors [&_.leadingIcon]:duration-100"
			leave-from-class="w-content"
			leave-to-class="w-8 [&_.trailing]:opacity-0 [&_input]:ps-0 [&_input]:pe-0 [&_input]:placeholder:opacity-0 [&_.leading]:ps-1.5 [&_.leadingIcon]:text-default"
			@after-leave="isButtonShown = true"
		>
			<div
				v-if="isExpanded"
				class="overflow-hidden"
			>
				<DInput
					ref="input"
					v-model="searchValue"
					size="sm"
					autofocus
					data-testid="listing-search-input"
					:placeholder="placeholder ?? $t('common.search')"
					:disabled
					:loading
					leading-icon="lucide:search"
					:ui="{ base: 'py-1.5', trailing: 'pe-1' }"
					@keydown.escape="collapseSearch"
					@blur="handleBlur"
				>
					<template #trailing>
						<DButton
							data-testid="listing-search-clear-button"
							variant="ghost"
							color="neutral"
							size="xs"
							icon="lucide:x"
							class="rounded-sm opacity-100"
							@click="searchValue ? clearSearch() : collapseSearch()"
						/>
					</template>
				</DInput>
			</div>
		</Transition>
		<DTooltip v-if="isButtonShown" :text="$t('common.search')">
			<DButton
				data-testid="listing-search-button"
				variant="outline"
				size="sm"
				color="neutral"
				icon="lucide:search"
				:aria-label="$t('common.search')"
				@click="expandSearch"
			/>
		</DTooltip>
	</div>
</template>
